import { EditButton } from "@components/buttons/edit";
import {
  SHBox,
  SHStack,
  SHTextField,
  SHTypography,
} from "@components/design-systems";
import { UserSVG } from "@components/svgs";
import { useFeeCalculatorEditFamilyMember } from "@hooks/useFeeCalculatorEditFamilyMember";
import { useUserPermissions } from "@hooks/userUserPermission";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useClickAway, useToggle } from "react-use";

type Props = {
  memberIndex: number;
  initName?: string | null;
  disabled?: boolean;
  onChange?: (newName: string) => void;
};
export const MemberCardName = ({
  memberIndex,
  initName,
  disabled,
  onChange,
}: Props) => {
  const { isSupplierGroup } = useUserPermissions();
  const {
    formMethods: { control },
    cardNameFieldPath,
  } = useFeeCalculatorEditFamilyMember({
    memberIndex,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isEditing, toggleEditing] = useToggle(false);
  const [name, setName] = useState(
    initName || `Family member/entity ${memberIndex + 1}`,
  );

  const onNameChange = (newValue: string) => {
    setName(newValue);
  };

  const debounceEditName = debounce((newValue: string) => {
    onChange?.(newValue);
  }, 300);

  const renderDisplayName = () => {
    return (
      <SHTypography
        disabled={disabled}
        variant="subtitle2"
        lineHeight={1.2}
        sx={{
          wordBreak: "break-all",
        }}
      >
        {name}
      </SHTypography>
    );
  };

  const renderEditName = () => {
    return (
      <Controller
        control={control}
        name={cardNameFieldPath}
        render={() => {
          return (
            <SHTextField
              value={name}
              onChange={(e) => {
                const newValue = e.target.value;
                onNameChange(newValue);
              }}
              sx={{
                "& .MuiInput-input": {
                  padding: "0 !important",
                  fontSize: "14px",
                  fontWeight: 500,
                },
                "& .MuiInput-root": {
                  marginTop: "0px !important",
                  height: "25px !important",
                  width: "164px",
                  paddingRight: "0px !important",
                },
              }}
            />
          );
        }}
      />
    );
  };

  useClickAway(wrapperRef, () => {
    if (isEditing) toggleEditing(false);
  });

  useEffect(() => {
    debounceEditName(name);
    return () => {
      debounceEditName.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return (
    <SHStack ref={wrapperRef} gap={0.5} maxWidth={"50%"} minWidth={"50%"}>
      <SHStack direction={"row"} alignItems={"center"} width={"100%"}>
        <SHBox>
          <UserSVG height={22} width={22} />
        </SHBox>
        <SHStack direction={"row"} gap={1} alignItems="center" width={"100%"}>
          {isEditing ? renderEditName() : renderDisplayName()}
          {isSupplierGroup && (
            <EditButton
              width="28px"
              height="28px"
              disabled={disabled}
              onClick={toggleEditing}
              sx={{
                svg: { marginTop: "2px" },
              }}
            />
          )}
        </SHStack>
      </SHStack>
    </SHStack>
  );
};
